import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bot, Send, Loader2, Sparkles, RefreshCw, Image, FileText, X, Download, Upload, Paperclip, Volume2 } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { supabase } from "@/integrations/supabase/client";
import { useTTS } from "@/hooks/useTTS";
import TTSControls from "./TTSControls";

interface Message {
  role: "user" | "assistant";
  content: string;
  images?: string[];
  attachments?: { type: string; url: string; name: string }[];
}

interface Attachment {
  type: "image" | "document";
  url: string;
  name: string;
  file?: File;
}

const ADMIN_CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-chat`;

const MAX_CONTEXT_MESSAGES = 200; // Maximum messages sent as context to AI

const AdminAIChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const tts = useTTS();

  // Load chat history on mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const { data, error } = await supabase
          .from("admin_chat_messages")
          .select("role, content, images, created_at")
          .order("created_at", { ascending: true })
          .limit(MAX_CONTEXT_MESSAGES);

        if (!error && data) {
          setMessages(data.map(m => ({
            role: m.role as "user" | "assistant",
            content: m.content,
            images: (m.images as string[]) || []
          })));
        }
      } catch (e) {
        console.error("Failed to load admin chat history:", e);
      } finally {
        setIsLoadingHistory(false);
      }
    };
    loadHistory();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const uploadFile = async (file: File): Promise<Attachment | null> => {
    setIsUploading(true);
    try {
      const fileType = file.type.startsWith("image/") ? "image" : "document";
      const fileName = `${Date.now()}_${file.name}`;
      const path = `uploads/${fileName}`;

      const { data, error } = await supabase.storage
        .from("admin-ai-attachments")
        .upload(path, file);

      if (error) {
        console.error("Upload error:", error);
        toast.error("فشل في رفع الملف");
        return null;
      }

      const { data: signed } = await supabase.storage
        .from("admin-ai-attachments")
        .createSignedUrl(path, 60 * 60 * 24 * 365);
      const publicUrl = signed?.signedUrl || "";

      return {
        type: fileType,
        url: publicUrl,
        name: file.name,
        file
      };
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("فشل في رفع الملف");
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`الملف ${file.name} كبير جداً (الحد الأقصى 10MB)`);
        continue;
      }

      // Validate file type
      const allowedTypes = [
        "image/jpeg", "image/png", "image/gif", "image/webp",
        "application/pdf", "text/plain", "text/markdown",
        "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error(`نوع الملف ${file.name} غير مدعوم`);
        continue;
      }

      const attachment = await uploadFile(file);
      if (attachment) {
        setAttachments(prev => [...prev, attachment]);
      }
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const sendMessage = async () => {
    if ((!input.trim() && attachments.length === 0) || isLoading) return;

    const userMessage: Message = { 
      role: "user", 
      content: input.trim(),
      attachments: attachments.map(a => ({ type: a.type, url: a.url, name: a.name }))
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setAttachments([]);
    setIsLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      // Save user message to DB
      await supabase.from("admin_chat_messages").insert({
        admin_id: session?.user?.id,
        role: "user",
        content: userMessage.content,
      });
      
      // Send last MAX_CONTEXT_MESSAGES as context
      const contextMessages = [...messages, { role: "user", content: userMessage.content }]
        .slice(-MAX_CONTEXT_MESSAGES);

      const response = await fetch(ADMIN_CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: contextMessages,
          attachments: userMessage.attachments
        }),
      });

      if (!response.ok) {
        let errorMsg = "فشل في الاتصال";
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorMsg;
        } catch {}
        throw new Error(errorMsg);
      }

      const data = await response.json();
      
      const assistantMessage: Message = {
        role: "assistant",
        content: data.content || "",
        images: data.images || []
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Save assistant message to DB
      await supabase.from("admin_chat_messages").insert({
        admin_id: session?.user?.id,
        role: "assistant",
        content: assistantMessage.content,
        images: assistantMessage.images,
      });

    } catch (error) {
      console.error("Admin chat error:", error);
      toast.error(error instanceof Error ? error.message : "حدث خطأ");
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = async () => {
    // Delete all admin chat messages from DB
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.id) {
      await supabase
        .from("admin_chat_messages")
        .delete()
        .eq("admin_id", session.user.id);
    }
    setMessages([]);
    setAttachments([]);
  };

  const downloadImage = (imageUrl: string, index: number) => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `generated-image-${index + 1}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const quickPrompts = [
    "أنشئ صورة لشعار عصري للمنصة",
    "أعطني ملخص عن حالة المنصة اليوم",
    "أنشئ تقريراً عن المعاملات هذا الأسبوع",
    "ما هي المعاملات المعلقة الآن؟",
    "اقترح تحسينات للمنصة",
  ];

  return (
    <Card className="glass border-border h-[700px] flex flex-col">
      <CardHeader className="border-b border-border pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-foreground flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <Bot className="w-5 h-5 text-primary" />
            </div>
            مساعد الذكاء الاصطناعي المتقدم
            <Sparkles className="w-4 h-4 text-primary" />
          </CardTitle>
          <div className="flex items-center gap-2">
            <TTSControls
              voices={tts.voices}
              selectedVoice={tts.selectedVoice}
              onVoiceChange={tts.setSelectedVoice}
              rate={tts.rate}
              onRateChange={tts.setRate}
              pitch={tts.pitch}
              onPitchChange={tts.setPitch}
              isSpeaking={tts.isSpeaking}
              onStop={tts.stop}
            />
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearChat}>
                <RefreshCw className="w-4 h-4 ml-1" />
                محادثة جديدة
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          يمكنني إنشاء الصور، تحليل الملفات، كتابة التقارير، وإدارة المنصة - انقر على أيقونة الصوت لسماع الردود
        </p>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        {/* Messages Area */}
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Bot className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                مرحباً بك في مساعد المسؤول الذكي المتقدم
              </h3>
              <p className="text-muted-foreground text-sm mb-2 max-w-md">
                يمكنني مساعدتك في تحليل البيانات، إنشاء الصور والمستندات، ومراجعة المعاملات
              </p>
              <div className="flex flex-wrap gap-2 justify-center mb-4">
                <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full flex items-center gap-1">
                  <Image className="w-3 h-3" /> إنشاء صور
                </span>
                <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full flex items-center gap-1">
                  <FileText className="w-3 h-3" /> تحليل مستندات
                </span>
                <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full flex items-center gap-1">
                  <Upload className="w-3 h-3" /> رفع ملفات
                </span>
              </div>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {quickPrompts.map((prompt, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => setInput(prompt)}
                  >
                    {prompt}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${
                    msg.role === "user" ? "flex-row-reverse" : ""
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-foreground"
                    }`}
                  >
                    {msg.role === "user" ? "أ" : <Bot className="w-4 h-4" />}
                  </div>
                  <div
                    className={`max-w-[85%] rounded-xl p-3 ${
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-foreground"
                    }`}
                  >
                    {/* Show user attachments */}
                    {msg.role === "user" && msg.attachments && msg.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {msg.attachments.map((att, i) => (
                          <div key={i} className="bg-background/20 rounded-lg p-2 flex items-center gap-2">
                            {att.type === "image" ? (
                              <img src={att.url} alt={att.name} className="w-16 h-16 object-cover rounded" />
                            ) : (
                              <div className="flex items-center gap-1">
                                <FileText className="w-4 h-4" />
                                <span className="text-xs">{att.name}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {msg.role === "assistant" ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none text-foreground">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                        {msg.content && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-2 text-xs"
                            onClick={() => tts.speak(msg.content)}
                            disabled={tts.isSpeaking}
                          >
                            <Volume2 className="w-3 h-3 ml-1" />
                            {tts.isSpeaking ? "جاري التحدث..." : "استمع"}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}

                    {/* Show generated images */}
                    {msg.images && msg.images.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {msg.images.map((img, imgIndex) => (
                          <div key={imgIndex} className="relative group">
                            <img
                              src={img}
                              alt={`Generated image ${imgIndex + 1}`}
                              className="max-w-full rounded-lg shadow-lg"
                            />
                            <Button
                              size="sm"
                              variant="secondary"
                              className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => downloadImage(img, imgIndex)}
                            >
                              <Download className="w-4 h-4 ml-1" />
                              تحميل
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                    <Bot className="w-4 h-4 text-foreground" />
                  </div>
                  <div className="bg-secondary rounded-xl p-3 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">جاري المعالجة...</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        {/* Attachments Preview */}
        {attachments.length > 0 && (
          <div className="px-4 py-2 border-t border-border bg-secondary/30">
            <div className="flex flex-wrap gap-2">
              {attachments.map((att, index) => (
                <div
                  key={index}
                  className="relative bg-background rounded-lg p-2 flex items-center gap-2 group"
                >
                  {att.type === "image" ? (
                    <img
                      src={att.url}
                      alt={att.name}
                      className="w-12 h-12 object-cover rounded"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                  )}
                  <span className="text-xs text-foreground max-w-[100px] truncate">
                    {att.name}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => removeAttachment(index)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 border-t border-border">
          <div className="flex gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*,.pdf,.txt,.md,.doc,.docx"
              multiple
              className="hidden"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading || isUploading}
              title="إرفاق ملف"
            >
              {isUploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Paperclip className="w-5 h-5" />
              )}
            </Button>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="اكتب سؤالك أو اطلب إنشاء صورة أو تحليل ملف... (Enter للإرسال)"
              className="min-h-[50px] max-h-[120px] resize-none bg-input border-border flex-1"
              disabled={isLoading}
            />
            <Button
              onClick={sendMessage}
              disabled={(!input.trim() && attachments.length === 0) || isLoading}
              className="h-auto gradient-primary"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminAIChat;
